import { RegistrationStatuses } from '@lumapps/events/types';
import { ColorPalette } from '@lumapps/lumx/react';
import { getUserFullName } from '@lumapps/user/utils/getUserFullName';

import { NOTIFICATIONS_CENTER } from '../../keys';
import { NotificationTypes, NotificationMeta, NotificationDescriptionParams } from '../../types';
import { getParentContentTitle } from '../contentUtils';
import { getEventTitle } from '../eventUtils';
import { getEmptyRoute, getEventLink } from '../linksUtils';

const getLearningEventRequestsStatusDescription = (params: NotificationDescriptionParams): string => {
    const { notification, senderDetails, translation, currentLanguage } = params;
    const eventTitle = getEventTitle(translation, notification);

    const registrationStatus = notification?.additionalContext?.registrationStatus;
    const reason = notification?.additionalContext?.reason;

    switch (registrationStatus) {
        case RegistrationStatuses.going:
            if (reason) {
                return translation.translateAndReplace(
                    NOTIFICATIONS_CENTER.EVENT_REQUEST_RESPONSE_POSITIVE_WITH_REASON,
                    {
                        AUTHOR_NAME: getUserFullName(senderDetails, currentLanguage),
                        REASON: reason,
                    },
                );
            }
            return translation.translateAndReplace(NOTIFICATIONS_CENTER.EVENT_REQUEST_RESPONSE_POSITIVE, {
                AUTHOR_NAME: getUserFullName(senderDetails, currentLanguage),
                EVENT_TITLE: eventTitle,
            });
        default:
            if (reason) {
                return translation.translateAndReplace(
                    NOTIFICATIONS_CENTER.EVENT_REQUEST_RESPONSE_NEGATIVE_WITH_REASON,
                    {
                        AUTHOR_NAME: getUserFullName(senderDetails, currentLanguage),
                        REASON: reason,
                    },
                );
            }
            return translation.translateAndReplace(NOTIFICATIONS_CENTER.EVENT_REQUEST_RESPONSE_NEGATIVE, {
                AUTHOR_NAME: getUserFullName(senderDetails, currentLanguage),
                EVENT_TITLE: eventTitle,
            });
    }
};

const getLearningEventRequestsDescriptions = (params: NotificationDescriptionParams): string => {
    const { notification, senderDetails, translation, currentLanguage } = params;
    const nbWaitingGuests = notification?.additionalContext?.nbWaitingGuests;

    if (nbWaitingGuests && nbWaitingGuests >= 1) {
        return translation.translateAndReplace(NOTIFICATIONS_CENTER.EVENT_REQUEST_MULTIPLE, {
            AUTHOR_NAME: getUserFullName(senderDetails, currentLanguage),
            NB_GUESTS: nbWaitingGuests,
        });
    }
    return translation.translateAndReplace(NOTIFICATIONS_CENTER.EVENT_REQUEST_SINGLE, {
        AUTHOR_NAME: getUserFullName(senderDetails, currentLanguage),
    });
};

const notificationTypeEvent: Record<string, NotificationMeta> = {
    [NotificationTypes.EventNew]: {
        hasThumbnail: true,
        iconProps: {
            color: ColorPalette.primary,
            icon: 'calendar',
        },
        getDescription: ({ notification, senderDetails, translation, currentLanguage }) => {
            const eventTitle = getEventTitle(translation, notification);

            const key = NOTIFICATIONS_CENTER.EVENT_NEW;

            return translation.translateAndReplace(key, {
                AUTHOR_NAME: getUserFullName(senderDetails, currentLanguage),
                CONTENT_TITLE: eventTitle,
                COMMUNITY_NAME: getParentContentTitle(translation, notification),
            });
        },
        getRoute: getEmptyRoute,
        getLinkAs: getEventLink,
    },
    [NotificationTypes.EventUpdated]: {
        hasThumbnail: true,
        iconProps: {
            color: ColorPalette.primary,
            icon: 'calendar',
        },
        getDescription: ({ notification, senderDetails, translation, currentLanguage }) => {
            const eventTitle = getEventTitle(translation, notification);

            const key = NOTIFICATIONS_CENTER.EVENTS_UPDATED;

            return translation.translateAndReplace(key, {
                AUTHOR_NAME: getUserFullName(senderDetails, currentLanguage),
                EVENT_TITLE: eventTitle,
            });
        },
        getRoute: getEmptyRoute,
        getLinkAs: getEventLink,
    },
    [NotificationTypes.EventInvitationCreated]: {
        hasThumbnail: true,
        iconProps: {
            color: ColorPalette.primary,
            icon: 'calendar',
        },
        getDescription: ({ notification, senderDetails, translation, currentLanguage }) => {
            const eventTitle = getEventTitle(translation, notification);

            const key = NOTIFICATIONS_CENTER.EVENT_INVITATION_CREATED;

            return translation.translateAndReplace(key, {
                AUTHOR_NAME: getUserFullName(senderDetails, currentLanguage),
                EVENT_TITLE: eventTitle,
            });
        },
        getRoute: getEmptyRoute,
        getLinkAs: getEventLink,
    },
    [NotificationTypes.EventMention]: {
        hasThumbnail: true,
        iconProps: {
            color: ColorPalette.primary,
            icon: 'calendar',
        },
        getDescription: ({ notification, senderDetails, translation, currentLanguage }) => {
            const eventTitle = getEventTitle(translation, notification);

            const key = NOTIFICATIONS_CENTER.CONTENT_MENTION_WITH_TITLE_DESCRIPTION;

            return translation.translateAndReplace(key, {
                AUTHOR_NAME: getUserFullName(senderDetails, currentLanguage),
                CONTENT_TITLE: eventTitle,
                COMMUNITY_NAME: getParentContentTitle(translation, notification),
            });
        },
        getRoute: getEmptyRoute,
        getLinkAs: getEventLink,
    },
    [NotificationTypes.EventRegistrationResponse]: {
        hasThumbnail: true,
        iconProps: {
            color: ColorPalette.primary,
            icon: 'school',
        },
        getDescription: (params) => getLearningEventRequestsStatusDescription(params),
        getRoute: getEmptyRoute,
        getLinkAs: getEventLink,
    },
    [NotificationTypes.EventRegistrationRequest]: {
        hasThumbnail: true,
        iconProps: {
            color: ColorPalette.primary,
            icon: 'school',
        },
        getDescription: (params) => getLearningEventRequestsDescriptions(params),
        getRoute: getEmptyRoute,
        getLinkAs: getEventLink,
    },
};

export { notificationTypeEvent };
